import * as AgGrid from "ag-grid-community";
import * as columns from "grid/columns";
import * as datasource from "grid/datasource";
import * as events from "utils/events";
import * as gridUtils from "grid/utils";
import * as http from "utils/http";
import * as dom from "utils/dom";
import * as notifications from "utils/notifications";
import { GridController } from "grid/grid_controller";
import { ACTIONS_COL_DEF, DATE_FILTER_COL_DEF, DEFAULT_COL_DEF } from "grid/columns";
import { onActionCellClicked } from "grid/row_actions";

export enum Action {
  CANCEL = "cancel",
  EDIT = "edit",
  UPDATE = "update",
}

const EDIT_ACTIONS = [Action.UPDATE, Action.CANCEL];
const LIST_ACTIONS = [Action.EDIT];

function transformRowActions(
  $agCell: HTMLElement,
  actions: Action[],
  transformFn: (el: HTMLButtonElement) => void,
): void {
  actions.forEach((action) => {
    const button = $agCell?.querySelector<HTMLButtonElement>(`button[data-action="${action}"]`);
    if (button) {
      transformFn(button);
    }
  });
}

function showEditMode($agCell: HTMLElement) {
  transformRowActions($agCell, LIST_ACTIONS, dom.hideElement);
  transformRowActions($agCell, EDIT_ACTIONS, dom.showElement);
}

function showNormalMode($agCell: HTMLElement) {
  transformRowActions($agCell, EDIT_ACTIONS, dom.hideElement);
  transformRowActions($agCell, LIST_ACTIONS, dom.showElement);
}

interface RejectPaymentOverride {
  alfa_account_number: string;
  rejected_payment_count: number;
  changed_by: string;
  date_of_change: string;
  rejected_payment_limit: number;
  override_reason: string;
  reject_payment_override_update_path: string;
}

export default class extends GridController<RejectPaymentOverride> {
  $searchInput: HTMLInputElement;

  override connect() {
    this.$searchInput = document.querySelector(".search-input");
    super.connect();

    const ctrl = this;
    events.subscribeOnEnter(this.$searchInput, () => ctrl.gridApi.onFilterChanged());
  }

  override buildGrid() {
    const ctrl = this;
    const { indexPath, i18nJson, paginationPageSize } = this.gridTarget.dataset;
    const colDefs: AgGrid.ColDef[] = [
      { field: "alfa_account_number", ...columns.DEFAULT_COL_DEF, ...columns.TEXT_FILTER_COL_DEF, sortable: true },
      { field: "rejected_payment_count", ...columns.NUMBER_COL_DEF, ...columns.NUMBER_FILTER_COL_DEF, sortable: true },
      { field: "changed_by", ...columns.DEFAULT_COL_DEF, ...columns.TEXT_FILTER_COL_DEF, sortable: false },
      { field: "date_of_change", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true },
      {
        field: "rejected_payment_limit",
        ...columns.NUMBER_COL_DEF,
        ...columns.NUMBER_FILTER_COL_DEF,
        editable: true,
        sortable: true,
      },
      { field: "override_reason", ...columns.DEFAULT_COL_DEF, editable: true, sortable: false },
      {
        ...ACTIONS_COL_DEF,
        cellClass: "actions-cell",
        onCellClicked(params) {
          onActionCellClicked<Action>(params, async function (action) {
            switch (action) {
              case Action.EDIT:
                return ctrl.handleEdit(params);
              case Action.UPDATE:
                return await ctrl.handleUpdate(params);
              case Action.CANCEL:
                return params.api.stopEditing(true);
            }
          });
        },
      },
    ];

    const dataSource = new datasource.Datasource({
      callbacks: {
        getSearchInput: () => (this.$searchInput ? this.$searchInput.value : ""),
      },
      indexPath,
      limit: paginationPageSize,
    });

    const options: AgGrid.GridOptions<RejectPaymentOverride> = {
      ...gridUtils.DEFAULT_GRID_OPTIONS,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<RejectPaymentOverride, any>),
        suppressKeyboardEvent(params) {
          if (params.event.code == events.KeyboardEvent.ENTER) {
            (params.editing ? ctrl.handleUpdate : ctrl.handleEdit)(params);
          }
          return false;
        },
      },
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, {
        i18n: JSON.parse(i18nJson),
      }),
      localeText: {
        ...columns.FILTER_TRANSLATIONS,
      },
      editType: "fullRow",
      onRowEditingStarted(params) {
        showEditMode(gridUtils.actionsCell(params));
      },
      onRowEditingStopped(params) {
        showNormalMode(gridUtils.actionsCell(params));
      },
      datasource: dataSource,
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, options);
    dataSource.gridApi = gridApi;
    return gridApi;
  }

  handleEdit(params: AgGrid.CellClickedEvent | AgGrid.SuppressKeyboardEventParams) {
    params.api.startEditingCell({ rowIndex: params.node.rowIndex, colKey: "rejected_payment_limit" });
  }

  async handleUpdate(params: AgGrid.CellClickedEvent | AgGrid.SuppressKeyboardEventParams) {
    params.api.stopEditing();
    const data = params.data as RejectPaymentOverride;
    const response = await http.httpPatch(data.reject_payment_override_update_path, {
      alfa_account_number: data.alfa_account_number,
      reject_payment_override: {
        rejected_payment_limit: data.rejected_payment_limit,
        override_reason: data.override_reason || "",
      },
    });

    if (response.ok) {
      const responseData = await response.json();
      notifications.success(responseData.success);
      params.api.stopEditing();
    } else {
      const responseData = await response.json();
      notifications.alert(responseData.error);
    }
  }
}
